import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { ProductType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import ESpinner from 'components/others/Spinner';
import TitleCounter from 'components/others/TitleCounter';
import ProductToolBar from './ProductToolBar';
import ProductsTable from './ProductsTable';
import { useProduct } from './context/ProductProvider';
import useCatalogCategoryFilter from '../CatalogCategory/model/useCatalogCategoryFilter';

const defaultProductFilterStatus = [1];

const Products = () => {
    const { t } = useTranslation(['Products', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const {
        showSpinner,
        setShowSpinner,
        productsDesc,
        productsOrder,
        setProductsOrder,
        setProductsDesc,
        listResetNonce,
        listRefreshNonce,
    } = useProduct();

    const [productsList, setProductsList] = useState<ProductType[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [filterVendorId, setFilterVendorId] = useState<number>();
    const [filterStatus, setFilterStatus] = useState<number[]>(defaultProductFilterStatus);
    const { filterCategoryIds, setFilterCategoryIds } = useCatalogCategoryFilter('product');

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchProducts = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;

        const listData = {
            search,
            order: `${productsOrder ? `order by ${productsOrder} ` : ''}${productsDesc ? 'DESC' : ''}`,
            page: page < 0 ? 1 : page || 1,
            onPage,
            status: filterStatus,
            vendors: filterVendorId ? [filterVendorId] : undefined,
            categoryIds: filterCategoryIds.length ? filterCategoryIds : undefined,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const resProducts = await apiProduct.getList(listData);
        if (!resProducts) return showToast(t('common:messError'), 'danger');
        setShowSpinner(false);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resProducts.paginator,
        }));

        setProductsList(resProducts.products);
    };

    const resetProductsList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setProductsOrder('');
        setProductsDesc(false);
        setFilterStatus(defaultProductFilterStatus);
        setFilterVendorId(undefined);
        setFilterCategoryIds([]);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: '',
            page: 1,
            onPage,
            status: defaultProductFilterStatus,
        };

        setShowSpinner(true);
        const resProducts = await apiProduct.getList(listData);
        setShowSpinner(false);

        if (!resProducts) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resProducts.paginator,
        }));

        setProductsList(resProducts.products);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, setProductsDesc, setProductsOrder, setShowSpinner, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetProductsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchProducts(true);
    });

    useEffect(() => {
        if (page === 1) fetchProducts(true);
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchProducts();
    }, [page, filterStatus, filterVendorId, filterCategoryIds, productsOrder, productsDesc, onPage]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    return (
        <Row>
            <Col className="col-sticky" sm={12}>
                <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                    <div>
                        <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                        <TitleCounter />
                    </div>
                    <div className="mt-2 mt-sm-0">
                        <ProductToolBar
                            filterVendorId={filterVendorId}
                            setFilterVendorId={(id) => {
                                setFilterVendorId(id);
                                setPage(1);
                            }}
                            filterStatus={filterStatus}
                            setFilterStatus={setFilterStatus}
                            filterCategoryIds={filterCategoryIds}
                            setFilterCategoryIds={setFilterCategoryIds}
                            fetchProducts={fetchProducts}
                        />
                    </div>
                </div>
            </Col>

            <Col sm={12}>
                 
                <div className="px-0">
                    {productsList ? (
                        <ProductsTable productsList={productsList} fetchProducts={fetchProducts} />
                    ) : (
                        <ESpinner />
                    )}
                </div>
                 
            </Col>
        </Row>
    );
};

export default Products;
